import { useEffect } from "react";

const WHEEL_GESTURE_GAP = 220;
const REVEAL_DISTANCE = 32;

export default function useMenuFooterReveal(footerRef, expanded, setExpanded) {
  useEffect(() => {
    const footer = footerRef.current;
    const root = footer?.parentElement;
    if (!footer || !root) return undefined;
    let frame;
    const update = () => {
      const rect = footer.getBoundingClientRect();
      const visibleHeight = Math.max(0, Math.min(rect.height, window.innerHeight - rect.top));
      // Lift the floating controls only by the visible footer, not by a blank spacer.
      root.style.setProperty("--mozzo-fiscal-footer-offset", `${visibleHeight}px`);
      if (expanded && rect.top > window.innerHeight + 80 && !footer.contains(document.activeElement)) {
        setExpanded(false);
      }
    };
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(scheduleUpdate) : null;
    observer?.observe(footer);
    if (expanded) footer.scrollIntoView({ block: "end", behavior: "auto" });
    update();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    window.visualViewport?.addEventListener("resize", scheduleUpdate);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      window.visualViewport?.removeEventListener("resize", scheduleUpdate);
      root.style.removeProperty("--mozzo-fiscal-footer-offset");
    };
  }, [footerRef, expanded, setExpanded]);

  useEffect(() => {
    if (expanded) return undefined;
    const footer = footerRef.current;
    const atEnd = () =>
      window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 3;
    const acceptsGesture = (target) => {
      if (!(target instanceof Element)) return false;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return false;
      if (target.closest('input, textarea, select, [contenteditable="true"]')) return false;
      if (target !== document.body && target !== document.documentElement &&
          !footer?.contains(target) && !target.closest("main, header")) return false;
      for (let node = target; node && node !== document.body; node = node.parentElement) {
        if (/(auto|scroll)/.test(window.getComputedStyle(node).overflowY) &&
            node.scrollHeight > node.clientHeight) return false;
      }
      return true;
    };
    // Eligibility is captured at the START of a gesture. Its momentum cannot open
    // the footer after that same gesture reaches the end of the menu.
    let lastWheel = -Infinity;
    let endReachedAt = atEnd() ? performance.now() : Infinity;
    let wheelEligible = false;
    let wheelDistance = 0;
    let touch = null;
    const trackEnd = () => {
      if (!atEnd()) endReachedAt = Infinity;
      else if (!Number.isFinite(endReachedAt)) endReachedAt = performance.now();
    };
    const onWheel = (event) => {
      const now = performance.now();
      if (now - lastWheel > WHEEL_GESTURE_GAP) {
        // Passive wheel events may arrive after the browser has already scrolled.
        wheelEligible = atEnd() && now - endReachedAt >= WHEEL_GESTURE_GAP &&
          acceptsGesture(event.target);
        wheelDistance = 0;
      }
      lastWheel = now;
      if (event.ctrlKey || event.deltaY <= 0 || Math.abs(event.deltaX) > event.deltaY) {
        wheelEligible = false;
        return;
      }
      const scale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
      wheelDistance += event.deltaY * scale;
      if (wheelEligible && atEnd() && wheelDistance >= REVEAL_DISTANCE) setExpanded(true);
    };
    const onTouchStart = (event) => {
      touch = event.touches.length === 1 && atEnd() && acceptsGesture(event.target)
        ? { x: event.touches[0].clientX, y: event.touches[0].clientY }
        : null;
    };
    const onTouchMove = (event) => {
      if (!touch || event.touches.length !== 1) {
        touch = null;
        return;
      }
      const dy = touch.y - event.touches[0].clientY;
      const dx = Math.abs(touch.x - event.touches[0].clientX);
      if (dy < 0 || dx > REVEAL_DISTANCE) touch = null;
      else if (dy >= REVEAL_DISTANCE && dy > dx && atEnd()) {
        touch = null;
        setExpanded(true);
      }
    };
    const clearTouch = () => { touch = null; };
    const onKeyDown = (event) => {
      if (!event.repeat && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey &&
          ["ArrowDown", "PageDown", "End", " "].includes(event.key) &&
          !event.target.closest?.("button, a") && atEnd() && acceptsGesture(event.target)) {
        setExpanded(true);
      }
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("scroll", trackEnd, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", clearTouch, { passive: true });
    window.addEventListener("touchcancel", clearTouch, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("scroll", trackEnd);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", clearTouch);
      window.removeEventListener("touchcancel", clearTouch);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [footerRef, expanded, setExpanded]);
}
